import { useEffect, useMemo, useState } from 'react';
import { Modal, Tabs, List, Spin, Empty, Button, Typography, Space } from 'antd';
import { ArrowLeftOutlined, CheckCircleFilled } from '@ant-design/icons';
import request from '@/utils/request';
import { thumb } from '@/utils/image';

interface ResourceItem {
  id: number;
  imageId: number;
  label: string | null;
  image: { id: number; accessUrl: string };
}
interface ExperimentRow {
  id: number;
  name: string;
  coverImageId: number | null;
}
interface RecordRow {
  id: number;
  modelSlug: string;
  prompt: string | null;
  outputImageIds: number[];
  createdAt: string;
}
interface ImageItem {
  id: number;
  accessUrl: string;
}

interface Props {
  open: boolean;
  mode: 'single' | 'multiple';
  /** Pre-selected imageIds (multiple mode only) */
  initialSelected?: number[];
  title?: string;
  onClose: () => void;
  onPick: (imageIds: number[]) => void;
}

const ImagePicker: React.FC<Props> = ({
  open,
  mode,
  initialSelected = [],
  title,
  onClose,
  onPick,
}) => {
  const [tab, setTab] = useState<'resources' | 'experiments'>('resources');
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Only re-seed on open→true transition. `initialSelected` defaults to a new
  // `[]` each render; including it in deps would re-reset selection after every
  // click (the "flash and cancel" bug).
  useEffect(() => {
    if (open) setSelected(new Set(initialSelected));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (imageId: number) => {
    if (mode === 'single') {
      setSelected(new Set([imageId]));
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(imageId)) next.delete(imageId);
      else next.add(imageId);
      return next;
    });
  };

  const handleOk = () => {
    if (selected.size === 0) {
      onClose();
      return;
    }
    onPick(Array.from(selected));
    setSelected(new Set());
  };

  const handleCancel = () => {
    setSelected(new Set());
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      title={title ?? (mode === 'single' ? '选择一张图片' : '选择图片（可多选）')}
      onOk={handleOk}
      okText={mode === 'single' ? '使用这张' : `确认使用 ${selected.size} 张`}
      okButtonProps={{ disabled: selected.size === 0 }}
      width={720}
      destroyOnClose
    >
      <Tabs
        activeKey={tab}
        onChange={(k) => setTab(k as 'resources' | 'experiments')}
        items={[
          {
            key: 'resources',
            label: '资源库',
            children: (
              <ResourcesTab
                open={open}
                selected={selected}
                onToggle={toggle}
                mode={mode}
              />
            ),
          },
          {
            key: 'experiments',
            label: '实验输出',
            children: (
              <ExperimentsTab
                open={open}
                selected={selected}
                onToggle={toggle}
                mode={mode}
              />
            ),
          },
        ]}
      />
    </Modal>
  );
};

const ImageTile: React.FC<{
  accessUrl: string;
  label?: string;
  selected: boolean;
  onClick: () => void;
}> = ({ accessUrl, label, selected, onClick }) => (
  <div
    onClick={onClick}
    style={{
      position: 'relative',
      aspectRatio: '1/1',
      borderRadius: 8,
      overflow: 'hidden',
      cursor: 'pointer',
      border: selected ? '2px solid #D97706' : '2px solid transparent',
      boxShadow: selected ? '0 0 0 2px rgba(217,119,6,.18)' : undefined,
      background: '#f1f5f9',
    }}
  >
    <img
      src={thumb(accessUrl, 140)}
      alt=""
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />
    {selected && (
      <CheckCircleFilled
        style={{
          position: 'absolute',
          top: 6,
          right: 6,
          fontSize: 20,
          color: '#D97706',
          background: '#fff',
          borderRadius: '50%',
        }}
      />
    )}
    {label && (
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '2px 6px',
          fontSize: 10,
          color: '#fff',
          background: 'rgba(0,0,0,.5)',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        {label}
      </div>
    )}
  </div>
);

const ResourcesTab: React.FC<{
  open: boolean;
  selected: Set<number>;
  onToggle: (imageId: number) => void;
  mode: 'single' | 'multiple';
}> = ({ open, selected, onToggle }) => {
  const [items, setItems] = useState<ResourceItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const res = await request.get<{ items: ResourceItem[] }>('/creator/resources?pageSize=100');
        setItems((res.data as unknown as { items: ResourceItem[] }).items);
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;
  if (items.length === 0) {
    return <Empty description="资源库为空，先到实验台右栏上传" style={{ padding: 40 }} />;
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: 10,
        maxHeight: 420,
        overflowY: 'auto',
        paddingRight: 4,
      }}
    >
      {items.map((r) => (
        <ImageTile
          key={r.id}
          accessUrl={r.image.accessUrl}
          label={r.label ?? undefined}
          selected={selected.has(r.imageId)}
          onClick={() => onToggle(r.imageId)}
        />
      ))}
    </div>
  );
};

const ExperimentsTab: React.FC<{
  open: boolean;
  selected: Set<number>;
  onToggle: (imageId: number) => void;
  mode: 'single' | 'multiple';
}> = ({ open, selected, onToggle }) => {
  const [experiments, setExperiments] = useState<ExperimentRow[]>([]);
  const [expLoading, setExpLoading] = useState(false);
  const [picked, setPicked] = useState<ExperimentRow | null>(null);
  const [records, setRecords] = useState<RecordRow[]>([]);
  const [recLoading, setRecLoading] = useState(false);
  const [urlMap, setUrlMap] = useState<Map<number, string>>(new Map());

  useEffect(() => {
    if (!open) return;
    (async () => {
      setExpLoading(true);
      try {
        const res = await request.get<{ items: ExperimentRow[] }>('/creator/experiments?pageSize=100');
        setExperiments((res.data as unknown as { items: ExperimentRow[] }).items);
      } finally {
        setExpLoading(false);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!picked) {
      setRecords([]);
      setUrlMap(new Map());
      return;
    }
    (async () => {
      setRecLoading(true);
      try {
        const res = await request.get<{ items: RecordRow[] }>(
          `/creator/experiments/${picked.id}/records?pageSize=200`,
        );
        const list = (res.data as unknown as { items: RecordRow[] }).items.filter(
          (r) => r.outputImageIds.length > 0,
        );
        setRecords(list);
        const ids = list.flatMap((r) => r.outputImageIds);
        if (ids.length > 0) {
          const imgRes = await request.get<{ items: ImageItem[] }>(
            `/creator/images?ids=${ids.join(',')}`,
          );
          const map = new Map<number, string>();
          for (const im of (imgRes.data as unknown as { items: ImageItem[] }).items) {
            map.set(im.id, im.accessUrl);
          }
          setUrlMap(map);
        }
      } finally {
        setRecLoading(false);
      }
    })();
  }, [picked]);

  const allOutputTiles = useMemo(() => {
    const out: Array<{ imageId: number; record: RecordRow }> = [];
    for (const r of records) {
      for (const imgId of r.outputImageIds) out.push({ imageId: imgId, record: r });
    }
    return out;
  }, [records]);

  if (!picked) {
    if (expLoading) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;
    if (experiments.length === 0) {
      return <Empty description="还没有实验，先到实验台创作吧" style={{ padding: 40 }} />;
    }
    return (
      <List
        style={{ maxHeight: 420, overflowY: 'auto' }}
        dataSource={experiments}
        renderItem={(exp) => (
          <List.Item
            style={{ cursor: 'pointer', padding: '10px 12px', borderRadius: 8 }}
            onClick={() => setPicked(exp)}
            actions={[<a key="pick">查看输出图 →</a>]}
          >
            <List.Item.Meta
              title={<Typography.Text strong>{exp.name}</Typography.Text>}
              description={null}
            />
          </List.Item>
        )}
      />
    );
  }

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => setPicked(null)}>
          返回实验列表
        </Button>
        <Typography.Text strong>{picked.name}</Typography.Text>
      </Space>
      {recLoading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
      ) : allOutputTiles.length === 0 ? (
        <Empty description="该实验还没有成功输出" style={{ padding: 40 }} />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: 10,
            maxHeight: 420,
            overflowY: 'auto',
            paddingRight: 4,
          }}
        >
          {allOutputTiles.map(({ imageId, record }) => (
            <ImageTile
              key={imageId}
              accessUrl={urlMap.get(imageId) ?? ''}
              label={record.modelSlug}
              selected={selected.has(imageId)}
              onClick={() => onToggle(imageId)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ImagePicker;
